import { useCallback, useEffect, useRef, useState } from 'react';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_CRITICAL, SELECTION_CHANGE_COMMAND } from 'lexical';
import './LinkPlugin.css';

function sanitizeUrl(url) {
  if (url === '') {
    return 'https://';
  }
  
  // S'assurer que l'URL contient un protocole
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  
  return url;
}

function FloatingLinkEditor() {
  const [editor] = useLexicalComposerContext();
  const [isOpen, setIsOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [lastSelection, setLastSelection] = useState(null);
  const [position, setPosition] = useState({ top: 150, left: 150 });
  const popupRef = useRef(null);
  const inputRef = useRef(null);
  const hasLinkRef = useRef(false);

  const updateLinkEditor = useCallback(() => {
    const editorState = editor.getEditorState();
    
    editorState.read(() => {
      const selection = $getSelection();
      
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        const linkNode = nodes.find(node => $isLinkNode(node) || (node.getParent() && $isLinkNode(node.getParent())));
        
        if (linkNode && $isLinkNode(linkNode)) {
          hasLinkRef.current = true;
          setLinkUrl(linkNode.getURL());
        } else if (linkNode && $isLinkNode(linkNode.getParent())) {
          hasLinkRef.current = true;
          setLinkUrl(linkNode.getParent().getURL());
        } else {
          hasLinkRef.current = false;
        }
        
        // Sauvegarder la sélection pour une utilisation ultérieure
        setLastSelection(selection.clone());
        
        // Mettre à jour la position uniquement si le popup est déjà ouvert
        if (isOpen && selection.getTextContent().length > 0) {
          // Obtenir l'intervalle de la sélection
          const range = selection.getTextContent().length > 0 ? 
            window.getSelection().getRangeAt(0) : null;
            
          if (range) {
            const rect = range.getBoundingClientRect();
            if (rect && rect.width > 0) {
              // Ajuster la position pour centrer le popup sur la sélection
              const editorElem = editor.getRootElement();
              if (editorElem) {
                const editorRect = editorElem.getBoundingClientRect();
                
                // Positionner le popup au-dessus de la sélection
                setPosition({
                  top: rect.top - 10 - (popupRef.current ? popupRef.current.offsetHeight : 100),
                  left: rect.left + rect.width / 2 - (popupRef.current ? popupRef.current.offsetWidth / 2 : 150)
                });
              }
            }
          }
        }
      }
    });
  }, [editor, isOpen]);

  // S'inscrit au changement de sélection
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        TOGGLE_LINK_COMMAND,
        (payload) => {
          if (payload === null) {
            // Commande pour supprimer un lien
            if (isOpen) {
              setIsOpen(false);
            }
            return false; // Laisser la commande continuer
          }
          
          if (payload === true) {
            // Ouvrir l'éditeur de lien
            updateLinkEditor();
            setIsOpen(true);
            
            // Focus sur l'input après l'ouverture
            setTimeout(() => {
              inputRef.current && inputRef.current.focus();
            }, 0);
            
            return true; // Empêcher la commande de continuer
          }
          
          if (typeof payload === 'string') {
            // Ajouter/modifier un lien avec l'URL spécifiée
            if (isOpen) {
              setIsOpen(false);
            }
            return false; // Laisser la commande continuer
          }
          
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [editor, updateLinkEditor, isOpen]);

  const confirmLink = () => {
    if (lastSelection) {
      // Restaurer la sélection précédente
      editor.update(() => {
        const finalUrl = sanitizeUrl(linkUrl);
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, finalUrl);
      });
    }
    setIsOpen(false);
  };

  const removeLink = () => {
    if (lastSelection) {
      // Restaurer la sélection précédente
      editor.update(() => {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      });
    }
    setIsOpen(false);
  };

  // Gérer l'appui sur Entrée/Échap dans l'input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmLink();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  if (!isOpen) {
    return null;
  }
  
  return (
    <div 
      className="link-editor" 
      ref={popupRef}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="link-input">
        <input
          ref={inputRef}
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://exemple.com"
        />
      </div>
      <div className="link-editor-buttons">
        {hasLinkRef.current && (
          <button 
            className="link-editor-button link-editor-button-delete" 
            onClick={removeLink}
          >
            Supprimer
          </button>
        )}
        <button 
          className="link-editor-button link-editor-button-cancel" 
          onClick={() => setIsOpen(false)}
        >
          Annuler
        </button>
        <button 
          className="link-editor-button" 
          onClick={confirmLink}
        >
          Confirmer
        </button>
      </div>
    </div>
  );
}

export default function LinkPlugin() {
  return <FloatingLinkEditor />;
} 